/**
 * How the rest of the app tells Hapi that something happened.
 *
 * A module with a listener on it rather than a React context, for one reason:
 * the thing that knows most about what just happened is `ToastProvider`, and it
 * sits above the router in `main.tsx` — above where a mascot can sensibly live.
 * Making Hapi a context would mean moving providers around to satisfy a
 * drawing, which is the tail wagging the dog. A module is importable from
 * anywhere and cares about none of it.
 *
 * There is deliberately no queue. If two things happen in the same second, the
 * second one wins: Hapi is a reaction, not a log, and a mascot working through
 * a backlog of old celebrations is a mascot nobody believes.
 */

import type { HapiPose } from '../components/hapi/Hapi'

export interface Mood {
  pose: HapiPose
  /** What he says in the bubble, if anything. Kept short — it is a bubble. */
  says?: string
  /** How long to hold it before going back to idle. */
  ms?: number
}

type Listener = (mood: Mood) => void

const listeners = new Set<Listener>()

/**
 * Tell Hapi something happened.
 *
 * Safe to call when no Hapi is mounted — on the sign-in screen, in a test —
 * and does nothing then, which is why call sites never have to check.
 */
export function cheer(mood: Mood): void {
  for (const listener of listeners) listener(mood)
}

export function onMood(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * The pose for a thing that just finished.
 *
 * One table rather than a pose argument at every call site, so that "marking a
 * piece posted" looks the same everywhere it can be done — and so that the
 * mascot's vocabulary stays small enough to mean something. A pose used for
 * everything is a pose that says nothing.
 */
export const DID: Record<string, Mood> = {
  posted: { pose: 'happy', says: 'Out the door.' },
  taskAdded: { pose: 'push', says: 'On the pile.' },
  taskDone: { pose: 'happy', says: 'Nice.' },
  monthClosed: { pose: 'happy', says: 'That is the month.' },
  chased: { pose: 'point', says: 'Asked them.' },
  sentToClient: { pose: 'point', says: 'Over to them.' },
  approved: { pose: 'happy', says: 'They said yes.' },
  /** Not blame. The mess is the enemy, which is the one thing `tangled` means. */
  wentWrong: { pose: 'tangled', ms: 2600 },
  thinking: { pose: 'think', ms: 60_000 }
}
